import { CheckIcon, ChevronUpDownIcon } from '@heroicons/react/20/solid'
import { Dispatch, SetStateAction, useEffect, useMemo, useRef, useState } from 'react'
import Keyboard, { KeyboardReactInterface } from 'react-simple-keyboard'
import { Virtuoso } from 'react-virtuoso'
import { User } from '../../../gql/graphql'
import { classNames } from '../../_helpers'

type PropTypes = {
  user: User[],
  selectedPerson: User | null
  setSelectedPerson: Dispatch<SetStateAction<User | null>> | ((user: User) => void);
  disabled?: boolean
  autoFocus?: boolean
  keyboard?: boolean
}

const getUserName = (user: User | null) => {
  if (!user) {
    return ''
  }

  return `${user.firstName ?? ''} ${user.surname ?? ''}`.trim()
}

const sortFunction = (a: User, b: User) => {
  const aName = getUserName(a).toLowerCase()
  const bName = getUserName(b).toLowerCase()

  if (aName < bName) return -1
  if (aName > bName) return 1

  return 0
}

export const AddUserInput = ({ user, selectedPerson, setSelectedPerson, disabled, autoFocus, keyboard }: PropTypes) => {
  const [query, setQuery] = useState('')
  const [open, setOpen] = useState(false)
  const [activeIndex, setActiveIndex] = useState(0)
  const keyboardRef = useRef<KeyboardReactInterface | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const rootRef = useRef<HTMLDivElement>(null)

  const selectedPersonId = selectedPerson?.id ?? null
  const selectedLabel = getUserName(selectedPerson)

  const filteredPeople = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase()

    if (normalizedQuery === '') {
      return []
    }

    return user
      .filter((candidate) => getUserName(candidate).toLowerCase().includes(normalizedQuery))
      .sort(sortFunction)
  }, [query, user])

  const showOptions = open && query.trim().length > 0 && filteredPeople.length > 0

  useEffect(() => {
    const handlePointerDown = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
        setOpen(false)
      }
    }

    document.addEventListener('mousedown', handlePointerDown)

    return () => {
      document.removeEventListener('mousedown', handlePointerDown)
    }
  }, [])

  useEffect(() => {
    setQuery('')
    setOpen(false)
    setActiveIndex(0)
  }, [selectedPersonId])

  useEffect(() => {
    if (!showOptions) {
      setActiveIndex(0)
      return
    }

    if (activeIndex >= filteredPeople.length) {
      setActiveIndex(0)
    }
  }, [activeIndex, filteredPeople.length, showOptions])

  const onKeyPress = (button: string) => {
    if (button === '{shift}' || button === '{lock}') {
      handleShift()
    }
  }

  function handleShift() {
    if (!keyboardRef.current) {
      return
    }

    const currentLayout = keyboardRef.current.options.layoutName
    const shiftToggle = currentLayout === 'default' ? 'shift' : 'default'

    keyboardRef.current.setOptions({
      layoutName: shiftToggle
    })
  }

  const handleSelect = (person: User) => {
    setSelectedPerson(person)
    setQuery('')
    setOpen(false)
    setActiveIndex(0)
    inputRef.current?.blur()
  }

  const handleInputChange = (value: string) => {
    if (disabled) {
      return
    }

    setQuery(value)
    setOpen(true)
  }

  const handleInputFocus = () => {
    if (disabled) {
      return
    }

    if (!query && selectedLabel && inputRef.current) {
      requestAnimationFrame(() => {
        inputRef.current?.select()
      })
    }

    if (query.trim().length > 0) {
      setOpen(true)
    }
  }

  const handleInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showOptions && event.key !== 'Escape' && event.key !== 'Tab') {
      if (event.key === 'ArrowDown' && filteredPeople.length > 0) {
        event.preventDefault()
        setOpen(true)
      }
      return
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault()
      setActiveIndex((current) => (current + 1) % filteredPeople.length)
      return
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault()
      setActiveIndex((current) => (current - 1 + filteredPeople.length) % filteredPeople.length)
      return
    }

    if (event.key === 'Enter' && filteredPeople[activeIndex]) {
      event.preventDefault()
      handleSelect(filteredPeople[activeIndex])
      return
    }

    if (event.key === 'Escape') {
      setOpen(false)
      return
    }
  }

  const inputValue = query || selectedLabel

  return (
    <div ref={rootRef} className="relative">
      <div className="relative">
        <input
          ref={inputRef}
          autoFocus={autoFocus}
          className="w-full rounded-md border border-gray-300 bg-white py-2 pl-3 pr-10 shadow-sm focus:border-primary focus:outline-none sm:text-sm disabled:bg-gray-200 disabled:opacity-50"
          value={inputValue}
          onChange={(event) => handleInputChange(event.target.value)}
          onFocus={handleInputFocus}
          onKeyDown={handleInputKeyDown}
          disabled={disabled || keyboard}
          role="combobox"
          aria-expanded={showOptions}
          aria-controls="add-user-input-listbox"
          aria-autocomplete="list"
          aria-activedescendant={showOptions ? `add-user-input-option-${activeIndex}` : undefined}
        />
        <button
          type="button"
          className="absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 focus:outline-none"
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => {
            if (disabled) {
              return
            }

            inputRef.current?.focus()
            if (query.trim().length > 0) {
              setOpen(true)
            }
          }}
        >
          <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
        </button>

        {showOptions && (
          <div className="absolute bottom-0 z-10 mt-1 mb-10 w-full rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-none sm:text-sm">
            <Virtuoso
              id="add-user-input-listbox"
              style={{ height: '15rem' }}
              data={filteredPeople}
              itemContent={(index, person) => {
                const isActive = index === activeIndex
                const isSelected = selectedPersonId === person.id

                return (
                  <div
                    id={`add-user-input-option-${index}`}
                    key={person.id}
                    role="option"
                    aria-selected={isSelected}
                    className={classNames(
                      'relative cursor-default select-none py-2 pl-8 pr-4',
                      isActive ? 'bg-primary text-white' : 'text-gray-900'
                    )}
                    onMouseEnter={() => setActiveIndex(index)}
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => handleSelect(person)}
                  >
                    <span className={classNames('block truncate', isSelected && 'font-semibold')}>
                      {getUserName(person)}
                    </span>

                    {isSelected && (
                      <span
                        className={classNames(
                          'absolute inset-y-0 left-0 flex items-center pl-1.5',
                          isActive ? 'text-white' : 'text-primary'
                        )}
                      >
                        <CheckIcon className="h-5 w-5" aria-hidden="true" />
                      </span>
                    )}
                  </div>
                )
              }}
            />
          </div>
        )}
      </div>
      {keyboard && (
        <div className={'mt-4 -mx-4'}>
          <Keyboard
            keyboardRef={(r) => {
              keyboardRef.current = r
            }}
            onChange={(value) => handleInputChange(value)}
            onKeyPress={onKeyPress}
            mergeDisplay
            layoutName={'default'}
            layout={{
              default: [
                'q w e r t z u i o p ü',
                'a s d f g h j k l ö ä',
                '{shift} z x c v b n m , . {backspace}',
                '{space}'
              ],
              shift: [
                'Q W E R T Y U I O P Ü',
                'A S D F G H J K L Ö Ä',
                '{shift} Z X C V B N M m , . {backspace}',
                '{space}'
              ],
              numbers: ['1 2 3', '4 5 6', '7 8 9', '{abc} 0 {backspace}']
            }}
            display={{
              '{space}': 'Leerzeichen',
              '{numbers}': '123',
              '{ent}': 'return',
              '{escape}': 'esc ⎋',
              '{tab}': 'tab ⇥',
              '{backspace}': '⌫',
              '{capslock}': 'caps lock ⇪',
              '{shift}': '⇧',
              '{controlleft}': 'ctrl ⌃',
              '{controlright}': 'ctrl ⌃',
              '{altleft}': 'alt ⌥',
              '{altright}': 'alt ⌥',
              '{metaleft}': 'cmd ⌘',
              '{metaright}': 'cmd ⌘',
              '{abc}': 'ABC'
            }}
          />
        </div>
      )}
    </div>
  )
}
